/**
 * LaunchForm — the shell of the reworked mortgagee launch form.
 *
 * It is CONTROLLED by ONE flat values record (`values` / `setValues`, the
 * drawer's existing state) in which the action slice lives at
 * `values[binding.actionsKey]` and everything else is run level. There is no
 * `{ runLevelValues, actions }` envelope on the wire between this form and its
 * host: whoever needs the two slices derives them with `splitLaunchValues`. That
 * is what lets the drawer's `handleLaunch(next)` use the record it is handed and
 * its `handleRetry()` — which takes no argument and reads the drawer's own state
 * — reach the same builder and therefore the same shape.
 *
 * `LaunchForm` does NOT assemble the payload. Pressing Launch calls
 * `onSubmit(values)`; `buildLaunchPayload` is the one assembler, wired by the
 * test harness in Phases 3–4 and by the drawer in Phase 5.
 *
 * The shell is a `<div>`, never a `<form>`: `JsonSchemaForm` renders a `<form>`
 * element per action card, and nested forms are invalid HTML and break submit
 * semantics.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import CustomTooltip from "~/common/components/CustomTooltip";
import type { ConfidenceLevel } from "~/common/workflows/components/JsonSchemaForm";
import {
  ADDRESS_PART_KEYS,
  decomposeMortgageePrefill,
} from "~/common/workflows/launch/addressParts";
import PolicyActionGroup from "~/common/workflows/launch/PolicyActionGroup";
import { defaultInterestType } from "~/common/workflows/launch/InterestTypeField";
import { replyDraftValue } from "~/common/workflows/launch/buildLaunchPayload";
import ReplyDraftControl from "~/common/workflows/launch/ReplyDraftControl";
import ReanalyseControl, {
  type ReanalyseResult,
} from "~/common/workflows/launch/ReanalyseControl";
import {
  OpenEmailLink,
  type OpenEmailResult,
} from "~/common/workflows/components/OpenEmailLink";
import { OLD_INTEREST_NAME_KEY } from "~/common/workflows/launch/ActionCard";
import {
  missingTermIdentitySources,
  resolveTermIdentityPlacement,
} from "~/common/workflows/launch/PolicyTermField";
import {
  mergeLaunchSlice,
  splitLaunchValues,
  type LaunchBinding,
} from "~/common/workflows/launch/launchSchemaBinding";
import {
  findPolicyOption,
  readPolicyOptions,
  readPolicyTermKey,
} from "~/common/workflows/launch/policyPrefillOptions";
import { validateLaunch } from "~/common/workflows/launch/validateLaunch";
import {
  PRIMARY_ACTION_BUTTON_CLASS,
  TYPE_BODY,
  TYPE_LABEL,
} from "~/common/workflows/paneTypography";

function isEmptyValue(value: unknown): boolean {
  // `""` is NOT empty: it is a deliberate clear by the AM, and refilling it is
  // the "cleared stays cleared" bug this form must never reintroduce.
  return value === undefined || value === null;
}

function isBlank(value: unknown): boolean {
  return isEmptyValue(value) || (typeof value === "string" && value === "");
}

function titleOf(
  schema: Record<string, unknown> | null,
  key: string,
  fallback: string,
): string {
  const props = schema?.properties as Record<string, unknown> | undefined;
  const def = props?.[key] as Record<string, unknown> | undefined;
  return typeof def?.title === "string" ? def.title : fallback;
}

/**
 * Action-slice normalisation — the ONLY place an action is ever created.
 *
 * The drawer's `values` state is seeded from FLAT prefill and has no action-list
 * key, so without this the first render has no `ActionCard` and the submitted
 * payload is `inputs[actionsKey] = []`: AC-7's "exactly one action" fails, AC-8
 * has nothing to populate, and the must_pass round-trip probe is unsatisfiable.
 *
 * Returns the NEXT values record, or `null` when nothing needs to change — so
 * the caller's effect is a no-op on the steady state and cannot loop.
 *
 * Three rules, in order:
 *
 *  1. **Create** (`actions` empty): take every key of the flat record that
 *     `declaredKeys.action` declares, decompose the record list's addresses into
 *     the four part keys the split State+ZIP line renders, pre-fill the declared
 *     interest type with the contract default, and write ONE action. Keys that
 *     moved into the action and are NOT also declared at run level are removed
 *     from run level — leaving them would make `buildLaunchPayload` report a
 *     dropped key on every single launch, which is how a REAL dropped-key
 *     warning gets ignored.
 *  2. **Fold** (`actions` non-empty): the drawer's shallow merge
 *     (`setValues(prev => ({ ...prev, ...prefill }))`) cannot write into an
 *     action object that already exists, and a child effect can run before the
 *     parent's, so a `[]`-to-one write may land one render before the spread.
 *     Whenever run level gains a declared ACTION key, copy it into the existing
 *     action IFF that action field is still empty (`undefined` / `null` /
 *     absent). Never refill a field the AM typed, never refill one they cleared
 *     (`""`), never add a second action.
 *  3. **Never one-to-two.** `binding.maxActions` is the only gate, and the only
 *     other way an action is rebuilt is RE-ANALYSE success (Phase 4), which
 *     drops `values[actionsKey]` so rule 1 runs again against the fresh brief.
 *
 * `splitLaunchValues` stays total and still never invents an action: reading and
 * creating are deliberately separate, because a `splitLaunchValues` that invented
 * one would also invent it for `handleRetry` and for every unit test that calls
 * it on a half-built record.
 */
export function normaliseLaunchValues(
  binding: LaunchBinding,
  values: Record<string, unknown>,
): Record<string, unknown> | null {
  const actionsKey = binding.actionsKey;
  if (!binding.isReshaped || !actionsKey) return null;

  const { runLevelValues, actions } = splitLaunchValues(binding, values);
  const actionKeys = binding.declaredKeys.action;
  const runKeys = binding.declaredKeys.runLevel;
  const recordsKey = binding.roles.interestRecords;
  const interestTypeKey = binding.roles.interestType;
  const fallbackType = defaultInterestType(binding.interestTypeValues);

  // Declared ACTION keys stranded at run level that the run level does NOT also
  // declare. `old_mortgagee_name` is deliberately unaffected: it is declared at
  // run level, so it is not a stray.
  const strays = Object.keys(runLevelValues).filter(
    (k) => actionKeys.has(k) && !runKeys.has(k),
  );

  if (actions.length === 0) {
    let seed: Record<string, unknown> = {};
    for (const key of Object.keys(runLevelValues)) {
      if (actionKeys.has(key)) seed[key] = runLevelValues[key];
    }
    if (recordsKey) seed = decomposeMortgageePrefill(seed, recordsKey);
    if (interestTypeKey && fallbackType && isBlank(seed[interestTypeKey])) {
      seed[interestTypeKey] = fallbackType;
    }
    const nextRun: Record<string, unknown> = { ...runLevelValues };
    for (const key of strays) delete nextRun[key];
    return { ...nextRun, [actionsKey]: [seed] };
  }

  let changed = strays.length > 0;
  const nextActions = actions.map((action, index) => {
    if (index !== 0) return action;
    const merged: Record<string, unknown> = { ...action };
    let touched = false;
    for (const key of Object.keys(runLevelValues)) {
      if (!actionKeys.has(key)) continue;
      const incoming = runLevelValues[key];
      // An empty incoming value carries no information; writing it would also
      // never settle (null over null re-triggers on every render).
      if (isBlank(incoming)) continue;
      if (!isEmptyValue(merged[key])) continue;
      merged[key] = incoming;
      touched = true;
    }
    if (interestTypeKey && fallbackType && isBlank(merged[interestTypeKey])) {
      merged[interestTypeKey] = fallbackType;
      touched = true;
    }
    if (!touched) return action;
    changed = true;
    return merged;
  });

  if (!changed) return null;
  const nextRun: Record<string, unknown> = { ...runLevelValues };
  for (const key of strays) delete nextRun[key];
  return { ...nextRun, [actionsKey]: nextActions };
}

/**
 * Project a flat, FIELD-NAME-keyed record (`fieldConfidence` / `fieldLoading`)
 * onto one slice.
 *
 * Handing the flat record straight to `ActionCard` is not wrong-LOOKING — it
 * type-checks and renders — it just silently drops every prefill confidence chip
 * the AM uses to decide what to re-check.
 */
export function projectFieldMap<T>(
  source: Record<string, T> | undefined,
  keys: ReadonlySet<string>,
  extra: readonly string[] = [],
): Record<string, T> | undefined {
  if (!source) return undefined;
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(source)) {
    if (keys.has(key) || extra.includes(key)) out[key] = value;
  }
  return out;
}

export interface LaunchFormProps {
  binding: LaunchBinding;
  values: Record<string, unknown>;
  /**
   * The drawer's own `useState` setter. It accepts a FUNCTIONAL update because
   * RE-ANALYSE needs one: the host merges the fresh brief into `values` while
   * this form's `onReanalyse` promise is still settling, so a drop computed
   * from the closure's `values` would silently clobber the brief it just
   * fetched. Every other write here passes a plain record, as before.
   */
  setValues: Dispatch<SetStateAction<Record<string, unknown>>>;
  /** ONE values record — exactly the render-prop signature the drawer declares. */
  onSubmit: (next: Record<string, unknown>) => void;
  submitting?: boolean;
  /** A precondition OUTSIDE the payload (SP pool empty, client unpicked, …). */
  submitDisabledReason?: string | null;
  /**
   * True while a background prefill is in flight. The create-rule waits it out:
   * a first-paint create against an empty list freezes an empty action that the
   * drawer's later shallow merge cannot enter. Suites that mount a complete
   * fixture as initial `values` omit it and the create-rule runs on first paint.
   */
  prefillLoading?: boolean;
  fieldConfidence?: Record<string, ConfidenceLevel>;
  fieldLoading?: Record<string, boolean>;
  /**
   * Host-supplied re-analysis. RE-ANALYSE renders only when it is provided —
   * each host decides per-brief whether to offer it (e.g. ora's
   * `workflows.triage.$messageId` route passes it only when the loaded
   * brief's `can_reanalyze` flag is true), so the control is absent when a
   * host declines to wire it up for the current brief, rather than present
   * and broken.
   *
   * The host owns the whole server round trip (flip `prefillLoading` true,
   * `reanalyzeEmail`, refresh `lastExtraction`, `reanalysePrefill`, merge). This
   * form owns exactly one thing on top of it: dropping the action list on
   * SUCCESS so the create-rule rebuilds from the fresh brief.
   */
  onReanalyse?: () => Promise<ReanalyseResult | void>;
  /**
   * Host-supplied hop to the email this brief was prepared from, rendered as
   * Open Email to the right of RE-ANALYSE. A host passes it only when it can
   * actually open that email and the email is not already on screen — the
   * pane opened on an ora notification is the case it exists for. A
   * `{ ok: false }` result is shown under the link instead of failing silently.
   */
  onOpenEmail?: () => OpenEmailResult | void;
  /** Extra run-level content from the host (oversight), rendered in RUN OPTIONS. */
  runLevelControls?: ReactNode;
  /**
   * The drawer-owned Epic sign-in fallback, placed right ABOVE the submit
   * button (pinned with it). The host passes null when there is nothing to
   * say — story 28633: sign-in shows only when the default is unavailable.
   */
  preFooter?: ReactNode;
  /** Client card and other host chrome that scrolls with the form fields. */
  leadingContent?: ReactNode;
  submitLabel?: string;
}

export default function LaunchForm({
  binding,
  values,
  setValues,
  onSubmit,
  submitting = false,
  submitDisabledReason = null,
  prefillLoading = false,
  fieldConfidence,
  fieldLoading,
  onReanalyse,
  onOpenEmail,
  runLevelControls,
  preFooter,
  leadingContent,
  submitLabel = "Approve & Run",
}: LaunchFormProps) {
  const actionsKey = binding.actionsKey;
  const [openEmailError, setOpenEmailError] = useState<string | null>(null);

  /**
   * True from the press of RE-ANALYSE until its action drop has been queued.
   *
   * STATE, not a ref, and it gates normalisation for a reason that cost a
   * failing test to find. A fresh brief arrives FLAT at run level while the
   * stale action is still there, and the fold rule's second half deletes
   * declared ACTION keys stranded at run level — so a normalisation pass
   * between the merge and the drop wipes the brief off run level (the stale
   * action's fields are non-empty, so nothing is folded first), and the
   * rebuilt action comes back EMPTY. Suppressing normalisation for the
   * duration makes the merge-then-drop pair atomic from the rule's point of
   * view. It is state so that clearing it re-runs the effect even on the
   * failure path, where no values change would otherwise trigger it.
   */
  const [reanalysing, setReanalysing] = useState(false);

  // ── normalisation ───────────────────────────────────────────────────────
  useEffect(() => {
    if (reanalysing) return;
    if (prefillLoading) {
      // Wait only when there is NOTHING to show. Cached analyze_email values
      // should paint immediately; an empty first-paint create would freeze an
      // empty action the later shallow merge cannot enter (R13).
      const { actions } = splitLaunchValues(binding, values);
      if (actions.length === 0) {
        const hasSeed = [...binding.declaredKeys.action].some(
          (k) => !isBlank(values[k]),
        );
        if (!hasSeed) return;
      }
    }
    const next = normaliseLaunchValues(binding, values);
    if (next) setValues(next);
  }, [binding, values, setValues, prefillLoading, reanalysing]);

  const { runLevelValues, actions } = useMemo(
    () => splitLaunchValues(binding, values),
    [binding, values],
  );

  const policyOptions = useMemo(
    () => readPolicyOptions(values),
    [values],
  );

  // ── writes ──────────────────────────────────────────────────────────────
  /**
   * Route a MIXED patch to the slice that DECLARES each key. `target_interest`
   * lands on the action; `old_mortgagee_name`, `policy_type` / `carrier_name` /
   * `carrier_id` and the freeform `policy_term_key` land at run level. No
   * control has to know where a key lives, and spreading a mixed patch onto one
   * level — which is silently wrong — becomes impossible.
   */
  const writePatch = useCallback(
    (index: number, patch: Record<string, unknown>) => {
      if (!actionsKey) return;
      const split = splitLaunchValues(binding, values);
      const nextRun: Record<string, unknown> = { ...split.runLevelValues };
      const nextActions = split.actions.map((a) => ({ ...a }));
      while (nextActions.length <= index) nextActions.push({});
      const target = nextActions[index];
      for (const [key, value] of Object.entries(patch)) {
        if (binding.declaredKeys.action.has(key)) target[key] = value;
        else nextRun[key] = value;
      }
      setValues({ ...nextRun, [actionsKey]: nextActions });
    },
    [actionsKey, binding, setValues, values],
  );

  /** One run-level key, written without disturbing the action slice. */
  const writeRunLevel = useCallback(
    (key: string, value: unknown) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [setValues],
  );

  /**
   * The host's re-analysis, plus the ONE thing this form owns on top of it.
   *
   * On SUCCESS the action list is dropped so the create-rule (above) rebuilds
   * one action from the merged fresh brief. Without the drop the create-rule
   * will not rebuild an action that already exists and the drawer's shallow
   * merge cannot write INTO one, so the fresh brief would land at run level
   * where no control reads it: the AM would see a "successful" re-analysis that
   * changed nothing — the silent-success class this control exists to remove.
   *
   * On FAILURE nothing is dropped and `values` is untouched. There is no
   * snapshot/restore, because a restore has a window in which the form is empty
   * and an emptied form reads as "re-analysed, found nothing".
   *
   * The drop is a FUNCTIONAL update: by the time this promise settles the host
   * has already merged the fresh brief into `values`, and a drop computed from
   * this closure's `values` would throw that merge away.
   */
  const handleReanalyse = useCallback(async (): Promise<
    ReanalyseResult | void
  > => {
    if (!onReanalyse) return;
    setReanalysing(true);
    try {
      const result = await onReanalyse();
      if (result && result.ok === false) return result;
      if (actionsKey) {
        setValues((prev) => {
          if (!(actionsKey in prev)) return prev;
          const next = { ...prev };
          delete next[actionsKey];
          return next;
        });
      }
      return result;
    } finally {
      // Queued AFTER the drop, so the render that clears the gate is the same
      // one that has no action list — the create-rule then rebuilds from the
      // merged brief in a single pass. On the failure path this is the only
      // thing that re-runs the effect at all.
      setReanalysing(false);
    }
  }, [actionsKey, onReanalyse, setValues]);

  const onChangeAction = useCallback(
    (index: number, next: Record<string, unknown>) => {
      if (!actionsKey) return;
      const split = splitLaunchValues(binding, values);
      const nextActions = split.actions.map((a, i) => (i === index ? next : a));
      while (nextActions.length <= index) nextActions.push({});
      nextActions[index] = next;
      setValues({ ...split.runLevelValues, [actionsKey]: nextActions });
    },
    [actionsKey, binding, setValues, values],
  );

  // ── validation ──────────────────────────────────────────────────────────
  const validation = useMemo(
    () => validateLaunch({ binding, runLevelValues, actions, policyOptions }),
    [binding, runLevelValues, actions, policyOptions],
  );

  const placement = useMemo(
    () => resolveTermIdentityPlacement(binding),
    [binding],
  );

  /**
   * Declared term-identity keys the term control cannot honestly write. Surfaced
   * exactly like `missingRoles` AND blocking, because writing a partial identity
   * silently is the 2026-08-05 wrong-term defect.
   */
  const missingSources = useMemo(() => {
    const seen = new Set<string>();
    for (const action of actions) {
      const flat = mergeLaunchSlice(binding, runLevelValues, action);
      const option = findPolicyOption(
        policyOptions,
        typeof flat.policy_number === "string" ? flat.policy_number : null,
        readPolicyTermKey(flat),
      );
      for (const key of missingTermIdentitySources(placement.keys, option)) {
        seen.add(key);
      }
    }
    return [...seen];
  }, [actions, binding, placement.keys, policyOptions, runLevelValues]);

  const blockingReason =
    validation.blockingReason ??
    (missingSources.length > 0
      ? "The selected policy term does not supply every declared term-identity " +
        `field (${missingSources.join(", ")}). Launching would send a partial ` +
        "term identity, which is how a change lands on the wrong term."
      : null) ??
    submitDisabledReason;

  // ── projections ─────────────────────────────────────────────────────────
  const actionConfidence = projectFieldMap(
    fieldConfidence,
    binding.declaredKeys.action,
    [...binding.declaredKeys.interestRecord, ...ADDRESS_PART_KEYS],
  );
  const actionLoading = projectFieldMap(
    fieldLoading,
    binding.declaredKeys.action,
    [...binding.declaredKeys.interestRecord, ...ADDRESS_PART_KEYS],
  );

  // ── run-level control state ─────────────────────────────────────────────
  // Read through the SAME rules the payload uses, so what the AM sees and what
  // the payload carries cannot disagree: reply draft defaults ON (an untouched
  // form must behave exactly as today), the override defaults UNSET (an
  // accidental default would silently opt every run out of waiting).
  const replyDraftKey = binding.roles.replyDraft;
  const replyDraftChecked = replyDraftValue(
    replyDraftKey ? runLevelValues[replyDraftKey] : undefined,
  );

  const errorLines: string[] = [];
  if (binding.missingRoles.length > 0) {
    errorLines.push(
      `The launch contract does not declare a field this form needs: ${binding.missingRoles.join(", ")}.`,
    );
  }
  if (binding.unboundRunLevelKeys.length > 0) {
    errorLines.push(
      `The launch contract declares fields this form has no control for: ${binding.unboundRunLevelKeys.join(", ")}. They cannot be set here.`,
    );
  }
  if (missingSources.length > 0) {
    errorLines.push(
      `The selected policy term supplies no value for: ${missingSources.join(", ")}.`,
    );
  }

  return (
    // NOT a <form>: each ActionCard's JsonSchemaForm renders one.
    // Pane layout: fields scroll, Approve & Run stays pinned.
    <div
      className="flex min-h-0 flex-1 flex-col"
      data-testid="launch-form"
    >
      {/* Story 28633: tight gaps so the whole form fits a ~440×720 pane. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        {errorLines.length > 0 ? (
          <div
            role="alert"
            className={`rounded-lg border border-primary-error-600 px-3 py-2 ${TYPE_BODY} text-primary-error-600`}
            data-testid="launch-form-errors"
          >
            {errorLines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        ) : null}

        {/* The host's client box and the launch page's title come first — the
            page reads client, title, then the form (story 28633). */}
        {leadingContent}

        {prefillLoading ? (
          <div
            role="status"
            className={`flex items-center gap-2 rounded-md border border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-1 ${TYPE_BODY} text-primary-gray-light-700`}
            data-testid="launch-prefill-loading"
          >
            <span
              className="inline-block h-3 w-3 flex-shrink-0 animate-spin rounded-full border border-primary-gray-light-300 border-t-primary-gray-light-700"
              aria-hidden="true"
            />
            <span className="min-w-0">
              Filling in policy and form details from this email…
            </span>
          </div>
        ) : null}

        {onReanalyse || onOpenEmail ? (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-start gap-3">
              {onReanalyse ? (
                <ReanalyseControl
                  onReanalyse={handleReanalyse}
                  disabled={submitting}
                />
              ) : null}
              {onOpenEmail ? (
                <OpenEmailLink
                  testId="launch-open-email"
                  // Level with the Re-analyse chip's label, not its border.
                  className="mt-[3px]"
                  onClick={() => {
                    const result = onOpenEmail();
                    setOpenEmailError(
                      result && result.ok === false ? result.message : null,
                    );
                  }}
                />
              ) : null}
            </div>
            {onOpenEmail && openEmailError ? (
              <p
                role="alert"
                className={`${TYPE_BODY} text-primary-warning-700`}
                data-testid="launch-open-email-error"
              >
                {openEmailError}
              </p>
            ) : null}
          </div>
        ) : null}

        <PolicyActionGroup
          binding={binding}
          runLevelValues={runLevelValues}
          actions={actions}
          placement={placement}
          policyOptions={policyOptions}
          writePatch={writePatch}
          onChangeAction={onChangeAction}
          submitting={submitting}
          fieldConfidence={actionConfidence}
          fieldLoading={actionLoading}
          actionErrors={validation.actionErrors}
          termFieldLabel={titleOf(
            placement.level === "action"
              ? binding.actionItemSchema
              : binding.runLevelSchema,
            placement.keys[0] ?? "policy_number",
            "Policy Number",
          )}
          effectiveDateLabel={titleOf(
            binding.actionItemSchema,
            binding.roles.effectiveDate ?? "effective_date",
            "Effective Date",
          )}
          oldInterestLabel={titleOf(
            binding.runLevelSchema,
            OLD_INTEREST_NAME_KEY,
            "Existing mortgagee",
          )}
        />

        {/* "Add another policy" is hidden until multi-policy launches work
            (story 28633). AddPolicyAffordance stays for that day. */}

        {/* RUN OPTIONS — outside every collapsible (AC-5). Skip-the-wait lives
            on the running drawer, not here. */}
        <div className="flex flex-col gap-1" data-testid="launch-run-level">
          <div className={`${TYPE_LABEL} text-primary-gray-light-600`}>
            Run options
          </div>
          <div className="flex flex-col divide-y divide-primary-gray-light-200 rounded-lg border border-primary-gray-light-200">
            {replyDraftKey ? (
              <div className="px-3 py-1">
                <ReplyDraftControl
                  fieldKey={replyDraftKey}
                  label="Draft the reply email"
                  checked={replyDraftChecked}
                  disabled={submitting}
                  onChange={(next) => writeRunLevel(replyDraftKey, next)}
                />
              </div>
            ) : null}
            {runLevelControls ? (
              <div className="px-3 py-1.5">{runLevelControls}</div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 flex-col gap-1 border-t border-primary-gray-light-200 bg-base-white pt-2">
        {preFooter ? (
          <div className="mb-1" data-testid="launch-pre-footer">
            {preFooter}
          </div>
        ) : null}
        {/* Why it is greyed out, in ora's tooltip, not a native title (story
            28633). The span is the hover target: a disabled button fires no
            pointer events. Node title + describeChild: no aria-label or
            native title lands on it; a null title never opens. Always
            wrapped, so the button is not remounted when it enables. */}
        <CustomTooltip
          id="launch-submit"
          title={blockingReason ? <span>{blockingReason}</span> : null}
          describeChild
          placement="top"
          arrow
          isOnModal
        >
          <span className="flex w-full">
            <button
              type="button"
              disabled={submitting || !!blockingReason}
              onClick={() => onSubmit(values)}
              // The pane's ONE primary-action look (Review & Approve's).
              className={`${PRIMARY_ACTION_BUTTON_CLASS} touch-target disabled:opacity-50`}
              data-testid="launch-submit"
            >
              {submitting ? "Starting…" : submitLabel}
            </button>
          </span>
        </CustomTooltip>
        {blockingReason ? (
          <p
            className={`${TYPE_BODY} text-primary-gray-light-700`}
            data-testid="launch-submit-blocked-reason"
          >
            {blockingReason}
          </p>
        ) : null}
      </div>
    </div>
  );
}
